/**
 * AeoStalenessWarning
 *
 * Displays a warning banner when AEO content is stale (uses outdated attribute values).
 * Shows which attributes changed and provides a "Regenerate AEO" button.
 *
 * Date: 2026-02-03
 */

import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Loader2, ArrowRight } from 'lucide-react';

interface StaleAttribute {
  name: string;
  oldValue: string;
  newValue: string;
  reason?: string;
}

interface AeoStalenessWarningProps {
  aeoIsStale: boolean;
  staleAttributes: StaleAttribute[];
  staleContentSections: string[];
  onRegenerateAeo: () => void;
  isRegenerating?: boolean;
  aeoGenerationId?: string | null;
}

export function AeoStalenessWarning({
  aeoIsStale,
  staleAttributes,
  staleContentSections,
  onRegenerateAeo,
  isRegenerating = false,
  aeoGenerationId,
}: AeoStalenessWarningProps) {
  if (!aeoIsStale || staleAttributes.length === 0) {
    return null;
  }

  const formatSectionName = (section: string): string => {
    const sectionNames: Record<string, string> = {
      faq: 'FAQ',
      summary: 'Summary',
    };
    return sectionNames[section] || section;
  };

  const formatValue = (value: string | unknown): string => {
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  return (
    <Alert variant="destructive" className="bg-amber-50 border-amber-300 dark:bg-amber-950/30 dark:border-amber-800">
      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      <AlertTitle className="text-amber-800 dark:text-amber-200 font-semibold">
        AEO Content May Be Outdated
      </AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <p className="text-amber-700 dark:text-amber-300 text-sm">
          The following attributes were updated after AEO generation:
        </p>

        <div className="space-y-2">
          {staleAttributes.map((attr, index) => (
            <div
              key={index}
              className="flex items-center gap-2 text-sm bg-amber-100 dark:bg-amber-900/40 px-3 py-2 rounded"
            >
              <span className="font-medium text-amber-800 dark:text-amber-200">
                {attr.name.replace(/_/g, ' ')}:
              </span>
              <span className="line-through text-amber-600 dark:text-amber-400">
                {formatValue(attr.oldValue)}
              </span>
              <ArrowRight className="h-3 w-3 text-amber-500" />
              <span className="font-medium text-green-700 dark:text-green-400">
                {formatValue(attr.newValue)}
              </span>
            </div>
          ))}
        </div>

        {staleContentSections.length > 0 && (
          <p className="text-amber-700 dark:text-amber-300 text-sm">
            The{' '}
            <span className="font-medium">
              {staleContentSections.map(formatSectionName).join(' and ')}
            </span>
            {' '}section{staleContentSections.length > 1 ? 's' : ''} still reference{staleContentSections.length > 1 ? '' : 's'} the old value{staleAttributes.length > 1 ? 's' : ''}.
          </p>
        )}

        {aeoGenerationId && (
          <Button
            onClick={onRegenerateAeo}
            disabled={isRegenerating}
            variant="outline"
            size="sm"
            className="mt-2 bg-amber-100 hover:bg-amber-200 border-amber-400 text-amber-800 dark:bg-amber-900/40 dark:hover:bg-amber-900/60 dark:border-amber-700 dark:text-amber-200"
          >
            {isRegenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate AEO
              </>
            )}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
